import React, { useRef, useEffect } from 'react';
import './FilmBackgroundSection.css';

interface FilmBackgroundSectionProps {
  videoSrc?: string;
  imageSrc?: string;
  className?: string;
}

const FilmBackgroundSection: React.FC<FilmBackgroundSectionProps> = ({ 
  videoSrc, 
  imageSrc, 
  className = '' 
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoSrc) return;

    // Ensure video is muted before attempting to play
    video.muted = true;
    
    let playPromise: Promise<void> | null = null;
    let isPlaying = false;
    
    // Use intersection observer to play only when visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Only play if not already playing and video is ready
            if (video.paused && video.readyState >= 3 && !isPlaying) {
              isPlaying = true;
              playPromise = video.play().then(() => {
                // Play succeeded
                isPlaying = true;
              }).catch(error => {
                console.log('Video autoplay was prevented:', error);
                isPlaying = false;
                playPromise = null;
              });
            }
          } else {
            // Only pause if we're currently playing
            if (isPlaying && !video.paused) {
              if (playPromise) {
                playPromise.then(() => {
                  if (!video.paused) {
                    video.pause();
                    isPlaying = false;
                  }
                }).catch(() => {
                  // Play was rejected, already not playing
                  isPlaying = false;
                });
              } else {
                video.pause();
                isPlaying = false;
              }
            }
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    // Wait for video to be ready before observing
    const handleCanPlayThrough = () => {
      observer.observe(video);
    };

    if (video.readyState >= 3) {
      observer.observe(video);
    } else {
      video.addEventListener('canplaythrough', handleCanPlayThrough, { once: true });
    }

    return () => {
      observer.disconnect();
      video.removeEventListener('canplaythrough', handleCanPlayThrough);
      // Clean up video state
      if (playPromise) {
        playPromise.then(() => {
          if (!video.paused && !video.ended) {
            video.pause();
          }
        }).catch(() => {
          // Play promise was rejected, video is not playing
        });
        playPromise = null;
      } else if (!video.paused && !video.ended) {
        video.pause();
      }
      isPlaying = false;
    };
  }, [videoSrc]);

  return (
    <div className={`film-background-section ${className}`}>
      {imageSrc && (
        <img 
          src={imageSrc} 
          alt="Background" 
          className="film-background-image"
        />
      )}
      {videoSrc && (
        <video
          ref={videoRef}
          className="film-background-video"
          src={videoSrc}
          muted
          loop
          playsInline
          // Remove autoPlay attribute to control it programmatically
        />
      )}
    </div>
  );
};

export default FilmBackgroundSection;